import { ReactElement } from "react";
import type { z, ZodType } from "zod";

// shamelessly taken from tanstack router ;-)
type ParsePathParams<T extends string, TAcc = never> = T &
    `${string}:${string}` extends never
    ? TAcc
    : T extends `${string}:${infer TPossiblyParam}`
      ? TPossiblyParam extends ""
          ? TAcc
          : TPossiblyParam & `${string}/${string}` extends never
            ? TPossiblyParam | TAcc
            : TPossiblyParam extends `${infer TParam}/${infer TRest}`
              ? ParsePathParams<TRest, TParam extends "" ? TAcc : TParam | TAcc>
              : never
      : TAcc;
type ResolveParams<TPath extends string> = TPath extends `${string}:${string}`
    ? Record<ParsePathParams<TPath>, string>
    : undefined;

interface Route<T extends string, P> {
    path: T;
    render: (val: ResolveParams<T>, props: P) => ReactElement;
    props: P;
    allowAnyone?: boolean;
}

export function createRoute<
    const T extends string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const P extends undefined | ZodType<any, any, any> = undefined
>(options: {
    path: T;
    render: (
        val: ResolveParams<T>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        props: P extends ZodType<any, any, any> ? z.infer<P> : never
    ) => ReactElement;
    props?: P;
    allowAnyone?: boolean;
}): Route<T, P> {
    return options as never;
}

// https://stackoverflow.com/questions/77189730/construct-a-mapped-type-from-an-array-of-generic-objects
export function mergeRoutes<T extends { path: string }[]>(
    routes: T
): {
    [U in T[number] as U["path"]]: U;
} {
    return routes as never;
}

// simplified route doesn't use a generic parameter for `props`.
// the required value of props is looked up dynamically within createLinkGenerator
interface SimplifiedRoute<T extends string> {
    render: (params: ResolveParams<T>, props: never) => ReactElement;
    props: unknown;
}

type SimplifiedRouteMap<T> = {
    [K in keyof T]: K extends string ? SimplifiedRoute<K> : never;
};

export interface CheckedLink {
    _checked: true; // this is a hack to make the type system check that the link is valid
    path: string;
}

export function createLinkGenerator<T, F>(_: F & SimplifiedRouteMap<T>) {
    return function <P extends string & keyof F>(
        path: P,
        params: ResolveParams<P>,
        _: F[P] extends SimplifiedRoute<P>
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              F[P]["props"] extends ZodType<any, any, any>
                ? z.infer<F[P]["props"]>
                : undefined
            : never
        // out of interest, this is the typing to get the output type of the render function: F[P] extends SimplifiedRoute<P> ? ReturnType<F[P]["render"]> : never
    ): CheckedLink {
        let replacedPath: string = path;
        if (params) {
            for (const key in params) {
                replacedPath = replacedPath.replace(
                    `:${key}`,
                    (params as Record<string, string>)[key] ?? ""
                );
            }
        }
        return {
            _checked: true,
            path: replacedPath
        };
    };
}

export function createReactComponentWithLink<TArgs, T, F>(
    simplifiedRouteMap: F & SimplifiedRouteMap<T>,
    inner: (link: CheckedLink, props: TArgs) => ReactElement
) {
    const generateLink = createLinkGenerator(simplifiedRouteMap);
    return function <P extends string & keyof F>(
        props: {
            path: P;
            params: ResolveParams<P>;
            props: F[P] extends SimplifiedRoute<P>
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  F[P]["props"] extends ZodType<any, any, any>
                    ? z.infer<F[P]["props"]>
                    : undefined
                : never;
        } & TArgs
    ): ReactElement {
        const link = generateLink(props.path, props.params, props.props);
        return inner(link, props);
    };
}
